
import React from 'react';
import { PenTool, Scissors, Repeat, Trash2, Zap, Palette, Layers, MousePointer2 } from 'lucide-react';
import { SimulationConfig, DrawerBrushType, DrawerSymmetryMode, DrawerColorMode } from '@/types';
import { SmartInput } from './SmartInput';
import { CollapsibleSection } from './CollapsibleSection';
import { Toggle } from './Toggle';
import { InfoPopup } from './InfoPopup';

interface DrawerSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  isLocked?: boolean;
  minPlan?: string;
  onUpgradeClick?: () => void;
}

export const DrawerSettingsSection: React.FC<DrawerSettingsSectionProps> = ({ 
    config, setConfig, isLocked, minPlan = "PRO", onUpgradeClick 
}) => {
  const brushes: DrawerBrushType[] = ['SOLID', 'NEON', 'SKETCHY', 'DOTTED', 'RAINBOW', 'CHROME', 'GHOST', 'GLOW', 'DASHED', 'ABSTRACT'];
  const symmetries: DrawerSymmetryMode[] = ['NONE', 'MIRROR_X', 'MIRROR_Y', 'MIRROR_XY', 'RADIAL'];
  const colorModes: DrawerColorMode[] = ['BALL', 'RAINBOW', 'VELOCITY', 'FIXED', 'HUE_SHIFT'];

  return (
    <CollapsibleSection 
        title="Painter Simulation Studio" 
        icon={PenTool} 
        darkMode={config.darkMode} 
        settingKey="drawer"
        isLocked={isLocked}
        lockRequiredPlan={minPlan as any}
        onUpgradeClick={onUpgradeClick}
        defaultOpen
    >
        <div className="flex flex-col gap-8 mt-6">
            
            {/* Core Controls */}
            <div className="flex flex-col gap-3">
                <SectionLabel label="Core Logic" icon={Zap} darkMode={config.darkMode} settingKey="drawerCoreLogic" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <ModifierCard 
                        label="Reverse Drawing"
                        settingKey="drawerReverseMode"
                        active={config.drawerReverseMode}
                        onToggle={() => setConfig(p => ({...p, drawerReverseMode: !p.drawerReverseMode}))}
                        darkMode={config.darkMode}
                        desc="Lines beam from ball to history"
                    />
                    <ModifierCard 
                        label="Ghost Mode"
                        settingKey="drawerLineDecay"
                        active={config.drawerLineDecay}
                        onToggle={() => setConfig(p => ({...p, drawerLineDecay: !p.drawerLineDecay}))}
                        darkMode={config.darkMode}
                        desc="Lines fade over time"
                    />
                    <ModifierCard 
                        label="Bounce Only"
                        settingKey="drawerOnlyOnBounce"
                        active={config.drawerOnlyOnBounce}
                        onToggle={() => setConfig(p => ({...p, drawerOnlyOnBounce: !p.drawerOnlyOnBounce}))}
                        darkMode={config.darkMode}
                        desc="Draw only when hitting walls"
                    />
                    <button 
                        onClick={() => {}} // This should trigger a clear in logic, handled via config reset or similar
                        className={`flex items-center justify-between p-4 rounded-2xl border transition-all duration-300 ${config.darkMode ? 'bg-white/5 border-white/5 hover:bg-white/10' : 'bg-zinc-100 border-transparent hover:bg-zinc-200'}`}
                    >
                        <div className="flex flex-col items-start gap-1">
                            <span className="text-[10px] font-black uppercase tracking-tight">Clear Canvas</span>
                            <span className="text-[8px] font-bold opacity-40 uppercase tracking-wider">Wipe current simulation</span>
                        </div>
                        <Trash2 className="w-4 h-4 opacity-50" />
                    </button>
                </div>
            </div>

            {/* Geometry & Symmetry */}
            <div className="flex flex-col gap-3">
                <SectionLabel label="Geometry & Symmetry" icon={Scissors} darkMode={config.darkMode} settingKey="drawerGeometrySymmetry" />
                <div className="flex flex-wrap gap-2 mb-2">
                    {symmetries.map(s => (
                        <BrushOption 
                            key={s} 
                            label={s.replace('_', ' ')} 
                            active={config.drawerSymmetryMode === s} 
                            onSelect={() => setConfig(p => ({...p, drawerSymmetryMode: s}))} 
                            darkMode={config.darkMode} 
                        />
                    ))}
                </div>
                {config.drawerSymmetryMode === 'RADIAL' && (
                    <SmartInput 
                        label="Symmetry Count" 
                        settingKey="drawerSymmetryCount" 
                        min={1} max={12} 
                        value={config.drawerSymmetryCount} 
                        onChange={(v: number) => setConfig(p => ({...p, drawerSymmetryCount: v}))} 
                        darkMode={config.darkMode} 
                    />
                )}
            </div>

            {/* Brush Selection */}
            <div className="flex flex-col gap-3">
                <SectionLabel label="Brush Style" icon={Layers} darkMode={config.darkMode} settingKey="drawerBrushStyle" />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                    {brushes.map(b => (
                        <BrushOption 
                            key={b} 
                            label={b} 
                            active={config.drawerBrushType === b} 
                            onSelect={() => setConfig(p => ({...p, drawerBrushType: b}))} 
                            darkMode={config.darkMode} 
                        />
                    ))}
                </div>
            </div>

            {/* Color Dynamics */}
            <div className="flex flex-col gap-3">
                <SectionLabel label="Color Dynamics" icon={Palette} darkMode={config.darkMode} settingKey="drawerColorDynamics" />
                <div className="flex flex-wrap gap-2 mb-2">
                    {colorModes.map(cm => (
                        <BrushOption 
                            key={cm} 
                            label={cm.replace('_', ' ')} 
                            active={config.drawerColorMode === cm} 
                            onSelect={() => setConfig(p => ({...p, drawerColorMode: cm}))} 
                            darkMode={config.darkMode} 
                        />
                    ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <SmartInput 
                        label="Line Visibility" 
                        settingKey="drawerLineOpacity" 
                        min={0} max={1} step={0.01} 
                        value={config.drawerLineOpacity} 
                        onChange={(v: number) => setConfig(p => ({...p, drawerLineOpacity: v}))} 
                        darkMode={config.darkMode} 
                    />
                    <SmartInput 
                        label="Glow Intensity" 
                        settingKey="drawerGlowIntensity" 
                        min={0} max={50} 
                        value={config.drawerGlowIntensity} 
                        onChange={(v: number) => setConfig(p => ({...p, drawerGlowIntensity: v}))} 
                        darkMode={config.darkMode} 
                    />
                </div>
            </div>

            {/* Advanced Physics & Constraints */}
            <div className="flex flex-col gap-3">
                <SectionLabel label="Physics & Constraints" icon={MousePointer2} darkMode={config.darkMode} settingKey="drawerPhysicsConstraints" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <SmartInput 
                        label="Stroke Thickness" 
                        settingKey="drawerLineThickness" 
                        min={1} max={20} 
                        value={config.drawerLineThickness} 
                        onChange={(v: number) => setConfig(p => ({...p, drawerLineThickness: v}))} 
                        darkMode={config.darkMode} 
                    />
                    <SmartInput 
                        label="Point Frequency" 
                        settingKey="drawerPointFrequency" 
                        min={0.1} max={10} step={0.1}
                        value={config.drawerPointFrequency} 
                        onChange={(v: number) => setConfig(p => ({...p, drawerPointFrequency: v}))} 
                        darkMode={config.darkMode} 
                    />
                    <SmartInput 
                        label="Max Length" 
                        settingKey="drawerMaxLength" 
                        min={100} max={5000} step={100}
                        value={config.drawerMaxLength} 
                        onChange={(v: number) => setConfig(p => ({...p, drawerMaxLength: v}))} 
                        darkMode={config.darkMode} 
                    />
                    <SmartInput 
                        label="Line Lifetime" 
                        settingKey="drawerLineLifetime" 
                        min={1} max={60} 
                        value={config.drawerLineLifetime} 
                        onChange={(v: number) => setConfig(p => ({...p, drawerLineLifetime: v}))} 
                        darkMode={config.darkMode} 
                    />
                </div>
            </div>

            {/* Attraction / Repulsion */}
            <div className="flex flex-col gap-3">
                <SectionLabel label="Path Attraction" icon={Repeat} darkMode={config.darkMode} settingKey="drawerPathAttraction" />
                <div className="flex gap-2 mb-2">
                    {['NONE', 'ATTRACT', 'REPEL'].map(m => (
                        <BrushOption 
                            key={m} 
                            label={m} 
                            active={config.drawerAttractionMode === m} 
                            onSelect={() => setConfig(p => ({...p, drawerAttractionMode: m as any}))} 
                            darkMode={config.darkMode} 
                        />
                    ))}
                </div>
                {config.drawerAttractionMode !== 'NONE' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <SmartInput 
                            label="Strength" 
                            settingKey="drawerAttractionStrength" 
                            min={0} max={0.5} step={0.01}
                            value={config.drawerAttractionStrength} 
                            onChange={(v: number) => setConfig(p => ({...p, drawerAttractionStrength: v}))} 
                            darkMode={config.darkMode} 
                        />
                        <SmartInput 
                            label="Radius" 
                            settingKey="drawerAttractionRadius" 
                            min={10} max={500} 
                            value={config.drawerAttractionRadius} 
                            onChange={(v: number) => setConfig(p => ({...p, drawerAttractionRadius: v}))} 
                            darkMode={config.darkMode} 
                        />
                    </div>
                )}
            </div>
        </div>
    </CollapsibleSection>
  );
};

const SectionLabel = ({ label, icon: Icon, darkMode, settingKey }: any) => (
    <div className="flex items-center gap-2 mb-1 px-1">
        <Icon className={`w-3.5 h-3.5 ${darkMode ? 'text-white/40' : 'text-zinc-400'}`} />
        <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${darkMode ? 'text-white/40' : 'text-zinc-400'}`}>{label}</span>
        {settingKey && <InfoPopup settingKey={settingKey} darkMode={darkMode} />}
    </div>
);

const BrushOption = ({ label, active, onSelect, darkMode }: any) => (
    <button 
        onClick={onSelect}
        className={`flex-1 flex flex-col items-center justify-center py-2 px-3 rounded-xl border transition-all duration-300 ${active ? (darkMode ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.2)]' : 'bg-cyan-600 border-cyan-600 text-white shadow-md') : (darkMode ? 'bg-white/5 border-transparent text-white/40 hover:bg-white/10' : 'bg-zinc-50 border-zinc-100 text-zinc-400 hover:bg-zinc-100')}`}
    >
        <span className="text-[7px] font-black uppercase tracking-tighter truncate w-full">{label}</span>
    </button>
);

const ModifierCard = ({ label, active, onToggle, darkMode, desc, settingKey }: any) => (
    <button 
        onClick={onToggle}
        className={`flex items-center justify-between p-4 rounded-2xl border transition-all duration-300 ${active ? (darkMode ? 'bg-cyan-500/10 border-cyan-500/20' : 'bg-white border-cyan-200 shadow-sm') : 'bg-zinc-50/50 border-transparent opacity-40 hover:opacity-60'}`}
    >
        <div className="flex flex-col items-start gap-1 text-left">
            <div className="flex items-center gap-2">
                <span className={`text-[10px] font-black uppercase tracking-tight ${active && !darkMode ? 'text-cyan-700' : ''}`}>{label}</span>
                {settingKey && <div onClick={e => e.stopPropagation()}><InfoPopup settingKey={settingKey} darkMode={darkMode} /></div>}
            </div>
            <span className="text-[8px] font-bold opacity-40 uppercase tracking-wider">{desc}</span>
        </div>
        <Toggle active={active} onClick={(e?: React.MouseEvent) => { e?.stopPropagation(); onToggle(); }} color={active ? (darkMode ? 'bg-cyan-500' : 'bg-cyan-600') : undefined} />
    </button>
);
